import { DialogComponent } from '@theme/dialog';

/**
 * Full-size view of a product image.
 *
 * Jewellery is the case where zoom is functional rather than decorative: a
 * ring is 20mm across, and the thing a shopper wants to see is the solder
 * seam and the stone setting. Prestige puts this behind a zoom button on the
 * gallery; so does this.
 *
 * Extends the drawer component, so the top layer, inertness, Escape and
 * focus return are all the platform's, exactly as for the cart drawer.
 *
 * The full-resolution image is only fetched when the dialog is first opened.
 * Preloading a 2000px image per product photograph would cost more than the
 * whole rest of the page.
 *
 * @extends {DialogComponent}
 */
class LightboxComponent extends DialogComponent {
  /**
   * Opens on the image the gallery is currently showing.
   *
   * @param {Event} event
   */
  openFromGallery(event) {
    event.preventDefault();

    const gallery = this.closest('.gallery') ?? document.querySelector('media-gallery-component');
    const active = gallery?.querySelector('[role="tab"][aria-selected="true"]');
    const mediaId = active?.getAttribute('data-media-id');

    if (mediaId) this.#show(mediaId);
    this.open();
  }

  /**
   * @param {string} mediaId
   */
  #show(mediaId) {
    for (const figure of this.querySelectorAll('[data-media-id]')) {
      const current = figure.getAttribute('data-media-id') === mediaId;
      figure.toggleAttribute('hidden', !current);

      // `src` is held in `data-src` until the image is actually wanted, so a
      // product with eight photographs does not fetch eight full-size files
      // for a dialog that may never be opened.
      if (!current) continue;

      const image = figure.querySelector('img[data-src]');
      if (image instanceof HTMLImageElement) {
        image.src = image.dataset.src ?? '';
        image.removeAttribute('data-src');
      }
    }
  }

  /**
   * Steps through the images without closing.
   *
   * @param {KeyboardEvent} event
   */
  navigate(event) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;

    event.preventDefault();

    const figures = Array.from(this.querySelectorAll('[data-media-id]'));
    const current = figures.findIndex((figure) => !figure.hasAttribute('hidden'));
    const next = figures[(current + step + figures.length) % figures.length];
    const mediaId = next?.getAttribute('data-media-id');

    if (mediaId) this.#show(mediaId);
  }
}

if (!customElements.get('lightbox-component')) {
  customElements.define('lightbox-component', LightboxComponent);
}
